// Modules
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';
import React from 'react';
import VelocityTransitionGroup from 'velocity-react/velocity-transition-group';
import _ from 'lodash';
import classNames from 'classnames';

import { CompanySelect, AgreementSelect, WorkOrderSelect } from 'components/forms/selects';
import { Form, FormConfirm, FormConfirmAction, FormConfirmActions } from 'components/forms/form-structure';
import { RadioInput, TextAreaInput, CheckboxInput, HiddenInput, DateInput } from 'components/forms/form-elements';
import { TICKET_TYPES } from 'constants';
import { TicketDescriptionInput } from 'components/forms/help-text';
import { convertArrayToOptions } from 'utils/input-utils';
import { slideClosed, slideOpen } from 'utils/transition-groups';
import Agreement from 'services/data/agreement';
import Person from 'services/data/person';
import PurchaseOrder from 'services/data/purchase-order';
import SVG from 'components/widgets/icons/svg';
import Ticket from 'services/data/ticket';
import TicketLink from 'components/models/ticket/ticket-link';
import TimeReport from 'services/data/time-report';
import requiredValidation from 'utils/required-validation';

const typeOptions = convertArrayToOptions(TICKET_TYPES);


class TicketForm extends React.Component {
  static propTypes = {
    close: PropTypes.func,
    closeOnSubmit: PropTypes.bool,
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    error: PropTypes.string,
    submitFailed: PropTypes.bool.isRequired,
    initialValues: PropTypes.object,
    formValues: PropTypes.object.isRequired,
    onSubmit: PropTypes.func,
    currentUser: PropTypes.object,
    change: PropTypes.func
  };

  constructor(props) {
    super(props);

    this.state = { possibleMatches: [], agreement: _.get(props.ticket, 'agreement') };
    this.setAgreement = this.setAgreement.bind(this);
  }

  componentWillMount() {
    const { initialValues } = this.props;

    if (!initialValues.id) return;
    
    if (_.isNil(initialValues.expectedCompletion) && initialValues.type === 'Help Desk') {
      const todaysDate = new Date();
      this.props.change('expectedCompletion', todaysDate);
    }
    
    // Get the current number of time reports and purchases so we know if we need
    // to confirm when we are updating
    return Promise.all([
      TimeReport.count({ where: { ticket: initialValues.id, invoice: null } }),
      PurchaseOrder.count({ where: { ticket: initialValues.id, invoice: null } })
    ])
      .then(([time, purchases]) => {
        this.props.change('ticketCurrentTime', time);
        this.props.change('ticketCurrentPurchases', purchases);
      });
  }

  componentWillReceiveProps(nextProps) {
    const oldType = this.props.formValues.type;
    const newType = nextProps.formValues.type;

    if (oldType !== newType) {
      const expectedCompletion = newType === 'Help Desk' ? new Date() : null;

      this.props.change('expectedCompletion', expectedCompletion);
    }

    if (_.isEqual(nextProps.formValues, this.props.formValues) || this.props.initialValues.id) return;
    const { description, company, agreement } = nextProps.formValues;
    const oldDescription = this.props.formValues.description;

    if (description === oldDescription) return;
    if (!description) return this.setState({ possibleMatches: [] });

    Ticket.find({
      where: {
        description: { contains: description },
        company: company || undefined,
        agreement: agreement || undefined, // prevent searching for tickets where agreement is specifically null
        status: { in: ['Active', 'Closed'] }
      },
      limit: 5
    })
      .then((tickets) => {
        this.setState({ possibleMatches: tickets });
      });
  }

  setAgreement(agreement) {
    this.setState({ agreement });
    this.props.change('timeDefaultBillable', agreement && agreement.timeDefaultBillable);
    if (agreement && !agreement.requiresWorkOrder) this.props.change('workOrder', null);
  }

  render() {
    const { close, handleSubmit, initialValues, formValues, currentUser, change, ...props } = this.props;
    const { agreement, possibleMatches } = this.state;
    const { timeDefaultBillable, updateTimeBillable, updateAgreement, ticketCurrentPurchases, ticketCurrentTime } = formValues;
    const editing = !!initialValues.id;
    const updatingBillable = editing && timeDefaultBillable !== initialValues.timeDefaultBillable;
    const requireBillableConfirm = updatingBillable && !!ticketCurrentTime;
    const billableLabel = timeDefaultBillable ? 'billable' : 'non-billable';
    const isLead = Person.isLead(currentUser);
    const newAgreementId = formValues.agreement;
    const oldAgreementId = initialValues.agreement;
    const changingAgreement = editing && newAgreementId !== oldAgreementId;
    const requireAgreementConfirm = changingAgreement && !!(ticketCurrentTime || ticketCurrentPurchases);
    const hideAgreement = formValues.type === 'Business Development' || formValues.type === 'Internal';
    const requiresWorkOrder = _.get(agreement, 'requiresWorkOrder');

    return (
      <div>
        <div className="form-header">
          <SVG src="svg/ticket.svg" className="ticket" />
          <h5 className="form-title">{ initialValues.id ? 'Edit' : 'Add'} Ticket</h5>
          { close && <SVG onClick={close} className="close" src="svg/close.svg" /> }
        </div>
        <Form onSubmit={handleSubmit} {..._.pick(props, ['submitting', 'error', 'submitFailed'])} customSubmitArea={(requireBillableConfirm || changingAgreement) && !isLead}>
          { isLead &&
            <div className="static-data-section static-data-section-below-header">
              <Field component={CheckboxInput} name="adminOnly" label="Praece Administrators Only" />
              <div>If checked, only Praece Administrators can access this ticket and ticket folder</div>
            </div>
          }

          <VelocityTransitionGroup leave={slideClosed} enter={slideOpen}>
            { (requireBillableConfirm || changingAgreement) && !isLead &&
              <div className="success-block notification-block">Great, we love keeping things organized! Changing agreements or whether time reports are billable can be dangerous from an accounting perspective, so please talk to a team lead or principal to get this done!</div>
            }
          </VelocityTransitionGroup>

          <Field component={CompanySelect} name="company" label="Company" />
          { !hideAgreement &&
            <Field component={AgreementSelect} onChangeFullValue={this.setAgreement} name="agreement" label="Agreement" company={formValues.company} searchParams={Agreement.activeQuery} />
          }

          <VelocityTransitionGroup leave={slideClosed} enter={slideOpen}>
            { requireAgreementConfirm && isLead &&
              <FormConfirm name="updateAgreement" title={'Update agreement for current time and purchase orders?'}>
                <div className="form-confirm-description positive">
                  Select <span className="form-confirm-emphasize positive">yes</span> to update all current time reports and purchase orders in this ticket to the new agreement.
                  Most commonly used for:
                  <ul>
                    <li>Mistaken ticketing of a triage item</li>
                    <li>Short-lived duplicate tickets</li>
                  </ul>
                </div>
                <div className="form-confirm-description">
                  Select <span className="form-confirm-emphasize">no</span> to leave all current time reports and purchase orders in this ticket as-is, new time reports and purchase orders will default to the new agreement.
                  Most commonly used for:
                  <ul>
                    <li>Rolling tickets into a new ongoing agreement</li>
                    <li>Merging long-lived related tickets</li>
                  </ul>
                </div>
                <FormConfirmActions>
                  <FormConfirmAction onClick={() => change('updateAgreement', true)} active={updateAgreement === true} label="Yes" svg="svg/check.svg">Move all time and purchases to the new agreement. <span className="form-confirm-emphasize positive">This will affect invoices, rates, and realization!</span></FormConfirmAction>
                  <FormConfirmAction onClick={() => change('updateAgreement', false)} active={updateAgreement === false} label="No" svg="svg/archived.svg">Leave all current time and purchases as-is. This will not affect invoices, rates, and realization.</FormConfirmAction>
                </FormConfirmActions>
              </FormConfirm>
            }
          </VelocityTransitionGroup>

          { requiresWorkOrder && <Field component={WorkOrderSelect} name="workOrder" label="Work Order" companyId={formValues.company} /> }

          <Field component={!hideAgreement ? CheckboxInput : HiddenInput} name="timeDefaultBillable" label="Time Reports are Billable by Default" />

          <VelocityTransitionGroup leave={slideClosed} enter={slideOpen}>
            { requireBillableConfirm && isLead &&
              <FormConfirm name="updateTimeBillable" title={`Update current time reports to be ${billableLabel}?`}>
                <div className="form-confirm-description positive">Select <span className="form-confirm-emphasize positive">yes</span> to update all current time reports in this ticket to be {billableLabel}.</div>
                <div className="form-confirm-description">Select <span className="form-confirm-emphasize">no</span> to leave all current time reports in this ticket as-is, new time reports will default to {billableLabel}.</div>
                <FormConfirmActions>
                  <FormConfirmAction onClick={() => change('updateTimeBillable', true)} active={updateTimeBillable === true} label="Yes" svg="svg/check.svg">Make all current time {billableLabel}. <span className="form-confirm-emphasize positive">This will affect invoices, rates, and realization!</span></FormConfirmAction>
                  <FormConfirmAction onClick={() => change('updateTimeBillable', false)} active={updateTimeBillable === false} label="No" svg="svg/archived.svg">Leave all current time as-is. This will not affect invoices, rates, and realization.</FormConfirmAction>
                </FormConfirmActions>
              </FormConfirm>
            }
          </VelocityTransitionGroup>

          <Field component={RadioInput} options={typeOptions} name="type" label="Type" />
          <Field
            component={TicketDescriptionInput}
            name="description"
            label="Description"
            placement="top"
            autoComplete="off"
          />
        
          <Field component={DateInput} name="expectedCompletion" label="Expected Completion Date" />
        
          { initialValues.status === 'Closed' &&
            <Field component={TextAreaInput} name="resolution" label="Resolution" />
          }
          { possibleMatches.length > 0 &&
            <div style={{ marginBottom: '35px', marginTop: '-10px' }}>
              Here are some open tickets with similar names. Go to one of these instead?
              {_.map(possibleMatches, ticket =>
                <TicketLink
                  key={ticket.id}
                  className={classNames('open-ticket', { deemphasize: ticket.status === 'Closed' })}
                  ticket={ticket}
                  onClick={() => {
                    if (close) close();
                  }}
                >
                  <SVG src="svg/ticket.svg" />
                  <div>
                    {ticket.description} { ticket.status === 'Closed' && <span>(Closed)</span> }
                  </div>
                </TicketLink>
              )}
            </div>
          }
        </Form>
      </div>
    );
  }
}

const ConnectedForm = connect((state, ownProps) => ({
  formValues: formValueSelector(ownProps.form)(state, 'company', 'agreement', 'description', 'type', 'timeDefaultBillable', 'updateTimeBillable', 'updateAgreement', 'ticketCurrentTime', 'ticketCurrentPurchases', 'expectedCompletion'),
  currentUser: state.auth.currentUser
}))(TicketForm);

export default reduxForm({
  validate: (values, props) => {
    const editing = props.initialValues.id;
    const hideAgreement = values.type === 'Business Development' || values.type === 'Internal';
    const requiresExpectedCompletion = _.includes(['Project', 'Help Desk'], values.type);

    const errors = requiredValidation(['description', 'type', 'company'], values);
    if (values.status !== 'Active') _.assign(errors, requiredValidation(['resolution'], values));
    if (!hideAgreement) _.assign(errors, requiredValidation(['agreement'], values));
    if (requiresExpectedCompletion) _.assign(errors, requiredValidation(['expectedCompletion'], values));

    const updatingBillable = values.timeDefaultBillable !== props.initialValues.timeDefaultBillable && editing && values.ticketCurrentTime;
    const updateTimeBillableUndefined = _.isUndefined(values.updateTimeBillable);

    if (updatingBillable && updateTimeBillableUndefined) errors.updateTimeBillable = 'Required';

    const newAgreementId = values.agreement;
    const oldAgreementId = props.initialValues.agreement;
    const changingAgreement = newAgreementId !== oldAgreementId && editing && (values.ticketCurrentTime || values.ticketCurrentPurchases);
    const updateAgreementUndefined = _.isUndefined(values.updateAgreement);

    if (changingAgreement && updateAgreementUndefined) errors.updateAgreement = 'Required';

    return errors;
  }
})(ConnectedForm);
